import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import {
  ArrowRight,
  Award,
  BriefcaseBusiness,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Code2,
  Eye,
  Globe2,
  KeyRound,
  LockKeyhole,
  Network,
  Radar,
  Rocket,
  ScanSearch,
  Shield,
  ShieldCheck,
  Sparkles,
  Terminal,
  Users,
  Wifi,
  Zap,
} from "lucide-react";

import {
  cyberSecurityFirstPageImg,
} from "../../assets";

import Layout from "../../components/shared/layout/layout";
import CourseDescription from "./component/course-description";


const CyberSecurity = () => {

  /* =========================================================
     ANIMATION
  ========================================================= */

  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30,
    },

    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };


  /* =========================================================
     FEATURES
  ========================================================= */

  const features = [
    {
      icon: ShieldCheck,
      title: "Defend Systems",
      text:
        "Understand how modern systems are protected against attacks, vulnerabilities and security threats.",
    },

    {
      icon: ScanSearch,
      title: "Find Vulnerabilities",
      text:
        "Learn how to identify weaknesses across applications, networks and infrastructure.",
    },

    {
      icon: Radar,
      title: "Detect Threats",
      text:
        "Develop practical skills for monitoring, investigating and responding to suspicious activity.",
    },

    {
      icon: LockKeyhole,
      title: "Think Like an Attacker",
      text:
        "Understand offensive security techniques so you can build stronger defensive strategies.",
    },
  ];


  /* =========================================================
     CURRICULUM
  ========================================================= */

  const curriculum = [
    {
      icon: Shield,
      title: "Security Fundamentals",
      text:
        "CIA triad, security principles, risk management, governance and compliance.",
    },

    {
      icon: Terminal,
      title: "Systems Administration",
      text:
        "Linux, Windows, permissions, users, servers, Active Directory and automation.",
    },

    {
      icon: Network,
      title: "Network Security",
      text:
        "Protocols, traffic analysis, VPNs, wireless security and network architecture.",
    },

    {
      icon: Radar,
      title: "Defensive Security",
      text:
        "SOC operations, SIEM, monitoring, incident response and threat detection.",
    },

    {
      icon: ScanSearch,
      title: "Offensive Security",
      text:
        "Ethical hacking, vulnerability assessment, penetration testing and exploitation.",
    },

    {
      icon: KeyRound,
      title: "Security Tools",
      text:
        "Wireshark, Burp Suite, Metasploit, SIEM platforms and practical security tooling.",
    },

    {
      icon: Globe2,
      title: "Web Security",
      text:
        "Understand common web vulnerabilities, application security and attack surfaces.",
    },

    {
      icon: Wifi,
      title: "Cloud & Infrastructure",
      text:
        "Security concepts for modern cloud environments and distributed infrastructure.",
    },

    {
      icon: Award,
      title: "Capstone & Certification",
      text:
        "Hands-on projects, certification preparation and career portfolio development.",
    },
  ];


  /* =========================================================
     CAREER PATHS
  ========================================================= */

  const careerPaths = [
    "Cybersecurity Analyst",
    "SOC Analyst",
    "Security Engineer",
    "Penetration Tester",
    "Ethical Hacker",
    "Network Security Analyst",
    "Cloud Security Associate",
    "IT Security Specialist",
  ];


  /* =========================================================
     SECURITY AREAS
  ========================================================= */

  const securityAreas = [
    {
      number: "01",
      title: "Prevent",
      text:
        "Build stronger systems before vulnerabilities become incidents.",
      icon: ShieldCheck,
    },

    {
      number: "02",
      title: "Detect",
      text:
        "Identify suspicious activity and security threats as they emerge.",
      icon: Eye,
    },

    {
      number: "03",
      title: "Respond",
      text:
        "Learn how security teams investigate and contain incidents.",
      icon: Zap,
    },

    {
      number: "04",
      title: "Recover",
      text:
        "Understand recovery, resilience and lessons learned after incidents.",
      icon: Rocket,
    },
  ];


  return (
    <Layout>

      <div className="overflow-hidden bg-white text-[#07141a]">


        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="relative min-h-[760px] overflow-hidden bg-[#050f14]">

          {/* Background glows */}

          <div className="pointer-events-none absolute inset-0">

            <div className="absolute -left-48 -top-40 h-[600px] w-[600px] rounded-full bg-[#59b5cd]/15 blur-[150px]" />

            <div className="absolute -right-48 top-20 h-[650px] w-[650px] rounded-full bg-[#CF385D]/10 blur-[160px]" />

            <div className="absolute bottom-[-250px] left-1/3 h-[600px] w-[600px] rounded-full bg-[#ecb32c]/10 blur-[150px]" />

          </div>


          {/* Grid */}

          <div
            className="pointer-events-none absolute inset-0 opacity-[0.045]"
            style={{
              backgroundImage:
                "linear-gradient(#59b5cd 1px, transparent 1px), linear-gradient(90deg, #59b5cd 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />


          {/* Scan line */}

          <motion.div
            animate={{
              y: ["0%", "100%"],
            }}
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: "linear",
            }}
            className="
              pointer-events-none
              absolute
              left-0
              right-0
              top-0
              h-px
              bg-gradient-to-r
              from-transparent
              via-[#59b5cd]/40
              to-transparent
            "
          />


          <div className="relative mx-auto max-w-7xl px-6 py-24 lg:py-32">

            <div className="grid items-center gap-16 lg:grid-cols-2">


              {/* =================================================
                  HERO LEFT
              ================================================= */}

              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="visible"
              >

                {/* Badge */}

                <div
                  className="
                    mb-7
                    inline-flex
                    items-center
                    gap-2
                    rounded-full
                    border
                    border-[#59b5cd]/25
                    bg-[#59b5cd]/10
                    px-4
                    py-2
                    text-sm
                    font-semibold
                    text-[#59b5cd]
                  "
                >

                  <ShieldCheck size={16} />

                  Zulfah Academy · Cybersecurity

                </div>


                {/* Heading */}

                <h1
                  className="
                    text-5xl
                    font-black
                    leading-[0.98]
                    tracking-tight
                    text-white
                    sm:text-6xl
                    lg:text-[74px]
                  "
                >

                  Defend.

                  <br />

                  <span className="text-[#59b5cd]">
                    Detect.
                  </span>

                  <br />

                  <span className="text-[#ecb32c]">
                    Secure.
                  </span>

                </h1>


                {/* Description */}

                <p
                  className="
                    mt-8
                    max-w-xl
                    text-lg
                    leading-relaxed
                    text-white/60
                    md:text-xl
                  "
                >
                  Learn how to protect digital systems, investigate threats,
                  discover vulnerabilities and build the skills required for
                  a career in cybersecurity.
                </p>


                {/* Buttons */}

                <div className="mt-9 flex flex-wrap gap-4">

                  <Link to="/get-started">

                    <button
                      className="
                        group
                        flex
                        items-center
                        gap-3
                        rounded-xl
                        bg-[#ecb32c]
                        px-7
                        py-4
                        font-bold
                        text-[#07141a]
                        shadow-lg
                        shadow-[#ecb32c]/20
                        transition-all
                        hover:bg-[#f2c34e]
                      "
                    >

                      Start Your Journey

                      <ArrowRight
                        size={20}
                        className="transition group-hover:translate-x-1"
                      />

                    </button>

                  </Link>


                  <a href="#curriculum">

                    <button
                      className="
                        flex
                        items-center
                        gap-2
                        rounded-xl
                        border
                        border-white/15
                        bg-white/5
                        px-7
                        py-4
                        font-semibold
                        text-white
                        transition
                        hover:bg-white/10
                      "
                    >

                      Explore Curriculum

                      <ChevronDown size={18} />

                    </button>

                  </a>

                </div>


                {/* Stats */}

                <div
                  className="
                    mt-12
                    flex
                    flex-wrap
                    gap-8
                    border-t
                    border-white/10
                    pt-8
                  "
                >

                  <div>

                    <p className="text-2xl font-bold text-white">
                      Practical
                    </p>

                    <p className="text-sm text-white/40">
                      Hands-on Training
                    </p>

                  </div>


                  <div>

                    <p className="text-2xl font-bold text-white">
                      Beginner
                    </p>

                    <p className="text-sm text-white/40">
                      Friendly
                    </p>

                  </div>


                  <div>

                    <p className="text-2xl font-bold text-white">
                      Projects
                    </p>

                    <p className="text-sm text-white/40">
                      Portfolio Ready
                    </p>

                  </div>

                </div>

              </motion.div>


              {/* =================================================
                  HERO VISUAL
              ================================================= */}

              <motion.div
                initial={{
                  opacity: 0,
                  scale: 0.92,
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                }}
                transition={{
                  duration: 0.9,
                }}
                className="relative"
              >

                {/* Main image */}

                <div
                  className="
                    relative
                    z-10
                    rounded-[30px]
                    border
                    border-white/10
                    bg-white/5
                    p-3
                    shadow-2xl
                    backdrop-blur-xl
                  "
                >

                  <div className="relative overflow-hidden rounded-[22px]">

                    <img
                      src={cyberSecurityFirstPageImg}
                      alt="Cybersecurity training at Zulfah Academy"
                      className="
                        h-[430px]
                        w-full
                        object-cover
                      "
                    />


                    <div
                      className="
                        absolute
                        inset-0
                        bg-gradient-to-t
                        from-[#050f14]
                        via-[#050f14]/10
                        to-transparent
                      "
                    />


                    {/* Status */}

                    <div className="absolute left-6 right-6 top-6">

                      <div
                        className="
                          flex
                          items-center
                          justify-between
                          rounded-xl
                          border
                          border-white/10
                          bg-black/30
                          px-4
                          py-3
                          backdrop-blur-md
                        "
                      >

                        <div className="flex items-center gap-3">

                          <span className="relative flex h-2.5 w-2.5">

                            <span className="absolute h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />

                            <span className="relative h-2.5 w-2.5 rounded-full bg-green-500" />

                          </span>

                          <span className="text-xs font-semibold text-white">
                            Security Lab Active
                          </span>

                        </div>


                        <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">
                          SECURE
                        </span>

                      </div>

                    </div>


                    {/* Bottom content */}

                    <div className="absolute bottom-6 left-6 right-6">

                      <div className="flex items-center gap-3">

                        <div
                          className="
                            flex
                            h-11
                            w-11
                            items-center
                            justify-center
                            rounded-xl
                            bg-[#59b5cd]
                            text-[#07141a]
                          "
                        >

                          <ShieldCheck size={22} />

                        </div>


                        <div>

                          <p className="font-bold text-white">
                            Cyber Defense Mode
                          </p>

                          <p className="text-sm text-white/50">
                            Detect → Investigate → Respond
                          </p>

                        </div>

                      </div>

                    </div>

                  </div>

                </div>


                {/* Floating card */}

                <motion.div
                  animate={{
                    y: [0, -12, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="
                    absolute
                    -left-8
                    bottom-10
                    z-20
                    hidden
                    items-center
                    gap-3
                    rounded-2xl
                    bg-white
                    p-4
                    shadow-2xl
                    md:flex
                  "
                >

                  <div
                    className="
                      flex
                      h-11
                      w-11
                      items-center
                      justify-center
                      rounded-xl
                      bg-[#59b5cd]/15
                    "
                  >

                    <Radar
                      size={22}
                      className="text-[#076c93]"
                    />

                  </div>


                  <div>

                    <p className="font-bold text-[#07141a]">
                      Threat Detection
                    </p>

                    <p className="text-xs text-gray-500">
                      Learn by doing
                    </p>

                  </div>

                </motion.div>


                {/* Floating attack card */}

                <motion.div
                  animate={{
                    y: [0, 10, 0],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="
                    absolute
                    -right-8
                    top-12
                    z-20
                    hidden
                    items-center
                    gap-3
                    rounded-2xl
                    bg-[#ecb32c]
                    p-4
                    shadow-xl
                    md:flex
                  "
                >

                  <ScanSearch size={25} />

                  <div>

                    <p className="font-bold">
                      Find Vulnerabilities
                    </p>

                    <p className="text-xs opacity-70">
                      Think like an attacker
                    </p>

                  </div>

                </motion.div>

              </motion.div>

            </div>

          </div>

        </section>


        {/* =====================================================
            COURSE SNAPSHOT
        ===================================================== */}

        <section className="relative z-20 -mt-12 px-6">

          <div className="mx-auto max-w-6xl">

            <div
              className="
                grid
                grid-cols-2
                overflow-hidden
                rounded-3xl
                border
                border-gray-100
                bg-white
                shadow-2xl
                md:grid-cols-4
              "
            >

              {[
                {
                  icon: Clock3,
                  title: "Flexible",
                  text: "Weekday & Weekend",
                },

                {
                  icon: ShieldCheck,
                  title: "Practical",
                  text: "Hands-on Labs",
                },

                {
                  icon: BriefcaseBusiness,
                  title: "Career",
                  text: "Industry Focused",
                },

                {
                  icon: Award,
                  title: "Certificate",
                  text: "Upon Completion",
                },
              ].map((item, index) => {

                const Icon = item.icon;

                return (

                  <div
                    key={index}
                    className="
                      flex
                      items-center
                      gap-4
                      border-b
                      border-gray-100
                      p-6
                      last:border-0
                      md:border-b-0
                      md:border-r
                      md:p-7
                      md:last:border-r-0
                    "
                  >

                    <div
                      className="
                        flex
                        h-11
                        w-11
                        shrink-0
                        items-center
                        justify-center
                        rounded-xl
                        bg-[#59b5cd]/10
                      "
                    >

                      <Icon
                        size={21}
                        className="text-[#076c93]"
                      />

                    </div>


                    <div>

                      <p className="font-bold">
                        {item.title}
                      </p>

                      <p className="text-xs text-gray-500">
                        {item.text}
                      </p>

                    </div>

                  </div>

                );

              })}

            </div>

          </div>

        </section>


        {/* =====================================================
            INTRO
        ===================================================== */}

        <section className="mx-auto max-w-7xl px-6 py-28">

          <motion.div
            variants={fadeUp}
            whileInView="visible"
            initial="hidden"
            viewport={{ once: true }}
            className="max-w-4xl"
          >

            <p className="mb-4 text-sm font-bold uppercase tracking-[0.25em] text-[#076c93]">
              Why Cybersecurity?
            </p>


            <h2
              className="
                text-4xl
                font-black
                leading-tight
                tracking-tight
                md:text-6xl
              "
            >

              Don't just use technology.

              <span className="block text-[#59b5cd]">
                Learn how to protect it.
              </span>

            </h2>


            <p
              className="
                mt-7
                max-w-3xl
                text-lg
                leading-relaxed
                text-gray-600
              "
            >
              Every organization depends on digital infrastructure.
              That creates an increasing demand for professionals who
              understand how attacks happen, how vulnerabilities are
              discovered and how systems can be protected.
            </p>

          </motion.div>


          {/* Feature cards */}

          <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-4">

            {features.map((feature, index) => {

              const Icon = feature.icon;

              return (

                <motion.div
                  key={index}
                  variants={fadeUp}
                  whileInView="visible"
                  initial="hidden"
                  viewport={{ once: true }}
                  transition={{
                    delay: index * 0.08,
                  }}
                  className="
                    group
                    rounded-3xl
                    border
                    border-gray-100
                    bg-white
                    p-7
                    transition-all
                    hover:-translate-y-1
                    hover:border-[#59b5cd]/40
                    hover:shadow-xl
                  "
                >

                  <div
                    className="
                      mb-6
                      flex
                      h-12
                      w-12
                      items-center
                      justify-center
                      rounded-xl
                      bg-[#59b5cd]/10
                      transition
                      group-hover:bg-[#ecb32c]
                    "
                  >

                    <Icon size={23} />

                  </div>


                  <h3 className="text-xl font-bold">
                    {feature.title}
                  </h3>


                  <p className="mt-3 text-sm leading-relaxed text-gray-500">
                    {feature.text}
                  </p>

                </motion.div>

              );

            })}

          </div>

        </section>


        {/* =====================================================
            SECURITY LIFECYCLE
        ===================================================== */}

        <section className="relative overflow-hidden bg-[#07141a]">

          <div className="pointer-events-none absolute -left-40 top-0 h-[500px] w-[500px] rounded-full bg-[#59b5cd]/10 blur-[130px]" />

          <div className="relative mx-auto max-w-7xl px-6 py-28">

            <div className="grid items-center gap-16 lg:grid-cols-2">


              <div>

                <p className="text-sm font-bold uppercase tracking-[0.25em] text-[#ecb32c]">
                  Think Like a Security Professional
                </p>


                <h2
                  className="
                    mt-5
                    text-4xl
                    font-black
                    leading-tight
                    text-white
                    md:text-6xl
                  "
                >

                  Security isn't just
                  <span className="text-[#59b5cd]">
                    {" "}technology.
                  </span>

                  <span className="block">
                    It's mindset.
                  </span>

                </h2>


                <p className="mt-7 max-w-xl text-lg leading-relaxed text-white/55">

                  You'll learn to approach systems from both sides:
                  understanding how attackers exploit weaknesses and
                  how defenders build resilient environments.

                </p>


                <div className="mt-9 space-y-4">

                  {[
                    "Hands-on security exercises",
                    "Realistic attack and defense scenarios",
                    "Security tools and lab environments",
                    "Mentorship and project feedback",
                  ].map((item, index) => (

                    <div
                      key={index}
                      className="flex items-center gap-3 text-white"
                    >

                      <CheckCircle2
                        size={20}
                        className="text-[#ecb32c]"
                      />

                      <span>{item}</span>

                    </div>

                  ))}

                </div>

              </div>


              {/* Security lifecycle */}

              <div className="grid grid-cols-2 gap-4">

                {securityAreas.map((item, index) => {

                  const Icon = item.icon;

                  return (

                    <motion.div
                      key={index}
                      whileHover={{
                        y: -5,
                      }}
                      className="
                        rounded-3xl
                        border
                        border-white/10
                        bg-white/[0.04]
                        p-6
                        backdrop-blur-xl
                        transition
                        hover:border-[#59b5cd]/30
                      "
                    >

                      <div className="flex items-center justify-between">

                        <span className="text-xs font-bold text-white/20">
                          {item.number}
                        </span>

                        <Icon
                          size={21}
                          className="text-[#59b5cd]"
                        />

                      </div>


                      <h3 className="mt-8 text-xl font-bold text-white">
                        {item.title}
                      </h3>


                      <p className="mt-3 text-sm leading-6 text-white/40">
                        {item.text}
                      </p>

                    </motion.div>

                  );

                })}

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            CURRICULUM
        ===================================================== */}

        <section
          id="curriculum"
          className="mx-auto max-w-7xl px-6 py-28"
        >

          <div className="mx-auto max-w-3xl text-center">

            <p className="text-sm font-bold uppercase tracking-[0.25em] text-[#076c93]">
              What You'll Learn
            </p>


            <h2
              className="
                mt-4
                text-4xl
                font-black
                tracking-tight
                md:text-6xl
              "
            >

              Your cybersecurity

              <span className="text-[#ecb32c]">
                {" "}toolkit.
              </span>

            </h2>


            <p className="mt-6 text-lg text-gray-500">
              A practical learning path covering security fundamentals,
              defensive operations, ethical hacking and modern
              infrastructure security.
            </p>

          </div>


          <div className="mt-16 grid gap-4 md:grid-cols-2 lg:grid-cols-3">

            {curriculum.map((item, index) => {

              const Icon = item.icon;

              return (

                <motion.div
                  key={index}
                  initial={{
                    opacity: 0,
                    y: 20,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    delay: index * 0.05,
                  }}
                  whileHover={{
                    y: -4,
                  }}
                  className="
                    group
                    rounded-2xl
                    border
                    border-gray-100
                    bg-[#f8fbfc]
                    p-6
                    transition
                    hover:border-[#59b5cd]/40
                    hover:bg-white
                    hover:shadow-lg
                  "
                >

                  <div className="flex items-start justify-between">

                    <div
                      className="
                        flex
                        h-11
                        w-11
                        items-center
                        justify-center
                        rounded-xl
                        bg-[#59b5cd]/10
                        transition
                        group-hover:bg-[#ecb32c]
                      "
                    >

                      <Icon size={21} />

                    </div>


                    <span className="text-xs font-bold text-gray-300">
                      {String(index + 1).padStart(2, "0")}
                    </span>

                  </div>


                  <h3 className="mt-6 text-lg font-bold">
                    {item.title}
                  </h3>


                  <p className="mt-2 text-sm leading-6 text-gray-500">
                    {item.text}
                  </p>

                </motion.div>

              );

            })}

          </div>

        </section>


        {/* =====================================================
            CAREER PATHS
        ===================================================== */}

        <section className="bg-[#f6fafb]">

          <div className="mx-auto max-w-7xl px-6 py-28">

            <div className="grid items-center gap-16 lg:grid-cols-2">


              {/* LEFT */}

              <div>

                <p className="text-sm font-bold uppercase tracking-[0.25em] text-[#076c93]">
                  Where It Can Take You
                </p>


                <h2
                  className="
                    mt-4
                    text-4xl
                    font-black
                    leading-tight
                    md:text-5xl
                  "
                >

                  One security foundation.

                  <span className="block text-[#59b5cd]">
                    Multiple career paths.
                  </span>

                </h2>


                <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-600">

                  Build a foundation that can prepare you for opportunities
                  across security operations, infrastructure, applications,
                  cloud environments and ethical hacking.

                </p>


                <Link to="/get-started">

                  <button
                    className="
                      mt-8
                      flex
                      items-center
                      gap-3
                      rounded-xl
                      bg-[#07141a]
                      px-7
                      py-4
                      font-bold
                      text-white
                      transition
                      hover:bg-[#076c93]
                    "
                  >

                    Start Learning

                    <ArrowRight size={19} />

                  </button>

                </Link>

              </div>


              {/* CAREERS */}

              <div className="grid gap-4 sm:grid-cols-2">

                {careerPaths.map((career, index) => (

                  <motion.div
                    key={index}
                    whileHover={{
                      y: -4,
                    }}
                    className="
                      rounded-2xl
                      border
                      border-gray-100
                      bg-white
                      p-6
                      shadow-sm
                      transition
                      hover:shadow-lg
                    "
                  >

                    <div
                      className="
                        mb-5
                        flex
                        h-10
                        w-10
                        items-center
                        justify-center
                        rounded-xl
                        bg-[#ecb32c]/15
                      "
                    >

                      <BriefcaseBusiness
                        size={19}
                        className="text-[#a87500]"
                      />

                    </div>


                    <h3 className="font-bold">
                      {career}
                    </h3>


                    <p className="mt-2 text-xs text-gray-400">
                      Career pathway
                    </p>

                  </motion.div>

                ))}

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            PROGRAM DETAILS
        ===================================================== */}

        <section className="relative overflow-hidden bg-[#f8fafb]">

          <div className="pointer-events-none absolute -left-32 top-20 h-72 w-72 rounded-full bg-[#59b5cd]/10 blur-[100px]" />

          <div className="pointer-events-none absolute -right-32 bottom-0 h-72 w-72 rounded-full bg-[#ecb32c]/10 blur-[100px]" />


          <div className="relative mx-auto max-w-6xl px-5 py-16 sm:px-6 sm:py-20">

            {/* Header */}

            <div className="mb-10 flex flex-col items-center text-center">

              <div
                className="
                  mb-4
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  border
                  border-[#59b5cd]/20
                  bg-white
                  px-3.5
                  py-1.5
                  shadow-sm
                "
              >

                <span className="h-1.5 w-1.5 rounded-full bg-[#59b5cd]" />

                <span className="text-[9px] font-black uppercase tracking-[0.22em] text-[#076c93]">
                  Program Details
                </span>

              </div>


              <h2
                className="
                  max-w-2xl
                  text-3xl
                  font-black
                  tracking-tight
                  text-[#07141a]
                  sm:text-4xl
                  md:text-5xl
                "
              >

                Ready to start{" "}

                <span className="text-[#ecb32c]">
                  securing the future?
                </span>

              </h2>


              <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-gray-500 sm:text-base">

                Explore programme options, pricing and enrollment details.

              </p>

            </div>


            {/* Course Description */}

            <motion.div
              initial={{
                opacity: 0,
                y: 20,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              transition={{
                duration: 0.6,
              }}
              className="
                mx-auto
                max-w-5xl
                overflow-hidden
                rounded-[26px]
                border
                border-gray-200/80
                bg-white
                shadow-[0_20px_60px_rgba(7,20,26,0.07)]
              "
            >

              <div
                className="
                  h-[3px]
                  w-full
                  bg-gradient-to-r
                  from-[#59b5cd]
                  via-[#ecb32c]
                  to-[#CF385D]
                "
              />

              <div className="p-4 sm:p-6 md:p-8">

                <CourseDescription />

              </div>

            </motion.div>

          </div>

        </section>


        {/* =====================================================
            FINAL CTA
        ===================================================== */}

        <section className="relative overflow-hidden bg-[#07141a]">

          <div className="pointer-events-none absolute inset-0">

            <div className="absolute -left-40 -top-40 h-[500px] w-[500px] rounded-full bg-[#59b5cd]/15 blur-[140px]" />

            <div className="absolute -bottom-40 -right-40 h-[500px] w-[500px] rounded-full bg-[#ecb32c]/10 blur-[140px]" />

          </div>


          <div className="relative mx-auto max-w-5xl px-6 py-28 text-center">

            <div
              className="
                mb-6
                inline-flex
                items-center
                gap-2
                font-semibold
                text-[#ecb32c]
              "
            >

              <ShieldCheck size={19} />

              Your security career starts here.

            </div>


            <h2
              className="
                text-4xl
                font-black
                leading-tight
                text-white
                md:text-6xl
              "
            >

              Ready to become

              <span className="text-[#59b5cd]">
                {" "}a cybersecurity professional?
              </span>

            </h2>


            <p
              className="
                mx-auto
                mt-6
                max-w-2xl
                text-lg
                text-white/50
              "
            >

              Build the technical skills, security mindset and practical
              experience needed to enter the cybersecurity industry.

            </p>


            <div className="mt-9 flex flex-wrap justify-center gap-4">

              <Link to="/get-started">

                <button
                  className="
                    flex
                    items-center
                    gap-3
                    rounded-xl
                    bg-[#ecb32c]
                    px-8
                    py-4
                    font-bold
                    text-[#07141a]
                    shadow-xl
                    transition
                    hover:scale-[1.03]
                  "
                >

                  Apply Now

                  <ArrowRight size={20} />

                </button>

              </Link>


              <a href="#curriculum">

                <button
                  className="
                    rounded-xl
                    border
                    border-white/15
                    bg-white/5
                    px-8
                    py-4
                    font-semibold
                    text-white
                    transition
                    hover:bg-white/10
                  "
                >

                  View Curriculum

                </button>

              </a>

            </div>

          </div>

        </section>

      </div>

    </Layout>
  );
};


export default CyberSecurity;